import {ExclamationIcon} from '@heroicons/react/outline';
import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  ConfirmationModal,
  Header,
  type SidebarTabItem,
  SidebarTabs,
  SpinningLoader,
} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import UserSecurityNotes from '@/components/SecurityNotes/UserSecurityNotes';
import useTabs from '@/components/Tabs/useTabs';
import type {AfriexBusiness} from '@/types/AfriexBusiness';
import {logRejection} from '@/utils/logRejection';

import BusinessAgreementTab from './Agreement/BusinessAgreementTab';
import {AllowedFeatureForm} from './AllowedFeature/AllowedFeatureForm';
import BusinessApiSettingsTab from './ApiSettings/BusinessApiSettingsTab';
import BusinessDetails from './BusinessDetails';
import BusinessRFITab from './BusinessRFITab';
import BusinessTransactionsView from './BusinessTransactionsView';
import BusinessFeeScheduleTab from './Fees/BusinessFeeScheduleTab';
import BusinessFeesTab from './Fees/BusinessFeesTab';
import BusinessInvoicesTab from './Invoices/BusinessInvoicesTab';
import BusinessLimitsTab from './Limits/BusinessLimitsTab';
import MoveMemberModal from './MoveMemberModal';
import BusinessRatesTab from './Rates/BusinessRatesTab';
import SubTabs from './SubTabs';
import BusinessTeamMembersTable from './TeamMembers/BusinessTeamMembersTable';
import TransferOwnerModal from './TransferOwnerModal';
import {useBusinessDetails} from './useBusinessDetails';
import BusinessVirtualAccountsTab from './VirtualAccounts/BusinessVirtualAccountsTab';

export const BusinessDetailsScreen = () => {
  const {
    businessWallet,
    isBusinessWalletLoading,
    businessDetails,
    businessUser,
    securityNotes,
    isLoadingSecurityNotes,
    hasMoreNotes,
    loadMoreSecurityNotes,
    isFetchingMore,
    transferOwner,
    isTransferringOwner,
    businessMembers,
    isLoadingBusinessMembers,
    fetchBusinessMembers,
    requestMemberMove,
    isMovingMember,
    searchTargetBusinesses,
    retireOrg,
    isRetiringOrg,
  } = useBusinessDetails();

  // Ownership recovery: reassign the org owner to another active member.
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  // Duplicate consolidation: request a member join another organization.
  const [isMoveMemberModalOpen, setIsMoveMemberModalOpen] = useState(false);
  // Manually triggered once an org is actually empty (moves are now
  // owner-approved and not synchronous, so there's no "just emptied" moment to
  // auto-detect right after sending a request).
  const [isRetireModalOpen, setIsRetireModalOpen] = useState(false);

  // `fetchBusinessMembers` toasts its own failures, so these handlers only fire
  // if that catch or the finally itself throws. Log rather than swallow
  // silently; the modal still opens either way, as it does today.
  const openTransferModal = () => {
    fetchBusinessMembers().catch(
      logRejection('Failed to load business members'),
    );
    setIsTransferModalOpen(true);
  };

  const openMoveMemberModal = () => {
    fetchBusinessMembers().catch(
      logRejection('Failed to load business members'),
    );
    setIsMoveMemberModalOpen(true);
  };

  // Eligible new owners: active members who aren't already the owner.
  const eligibleMembers = businessMembers.filter(
    (member) =>
      member.id !== businessUser?.id &&
      !member.meta?.flags?.isDeactivated &&
      !member.meta?.flags?.isDeleted,
  );
  // Team Members section + move-member modal: all active members, owner
  // included (the owner is exactly who gets moved when consolidating a
  // duplicate one-person org).
  const activeTeamMembers = businessMembers.filter(
    (member) =>
      !member.meta?.flags?.isDeactivated && !member.meta?.flags?.isDeleted,
  );

  // NOTE: do not wrap this in useMemo keyed on a partial dependency list.
  // The tab content renders live state (businessWallet, isBusinessWalletLoading,
  // securityNotes, ...); memoizing on [businessDetails] alone caused the balance
  // to freeze on "Loading..." whenever the wallet request resolved after the
  // details request. Rebuilding the array each render is cheap and correct.
  const businessTabs: SidebarTabItem[] = [
    {
      id: 'all',
      title: 'Details',
      group: 'Overview',
      content: (
        <>
          <BusinessDetails
            business={businessDetails as AfriexBusiness}
            person={businessUser}
          />
          {/* Security Notes */}
          <div className="px-3 pt-3 mt-8">
            <h3 className="font-semibold text-gray-900 dark:text-gray-300 mb-2">
              {'Security'.toLocaleUpperCase()}
            </h3>
            <hr className="mb-5 dark:border-dark-borderMid" />
            <UserSecurityNotes
              dataCy="business-details"
              securityNotes={securityNotes}
              isLoadingSecurityNotes={isLoadingSecurityNotes}
              hasMoreNotes={hasMoreNotes}
              loadMoreSecurityNotes={loadMoreSecurityNotes}
              isFetchingMore={isFetchingMore}
            />
          </div>

          {/* Team Members */}
          <div className="px-3 pt-3 mt-8">
            <h3 className="font-semibold text-gray-900 dark:text-gray-300 mb-2">
              {'Team Members'.toLocaleUpperCase()}
            </h3>
            <hr className="mb-5 dark:border-dark-borderMid" />
            <BusinessTeamMembersTable
              dataCy="business-details"
              members={activeTeamMembers}
              isLoading={isLoadingBusinessMembers}
            />
          </div>

          {/* Danger Zone: all three actions here can change who belongs to this
          org or freeze it outright, so they're kept separate from the normal
          tab content and visible regardless of which tab is active. The whole
          section — heading included — is gated, not just the buttons: there's
          no point showing a "Danger Zone" heading over an empty box to
          someone who can't act on anything inside it. */}
          <PermissionsProvider
            permission={AfriexAdminPermission.BusinessDangerZoneManage}
          >
            <div className="px-3 pt-3 mt-8">
              <h3 className="flex items-center gap-2 font-semibold text-failed-500 mb-2">
                {'Danger Zone'.toLocaleUpperCase()}{' '}
                <ExclamationIcon className="w-6 h-6 text-failed-500" />
              </h3>
              <hr className="mb-5 dark:border-dark-borderMid" />
              <div className="flex md:justify-end gap-2">
                <Button
                  variant="outline"
                  colorScheme="orange"
                  onClick={openMoveMemberModal}
                >
                  Move member to another org
                </Button>
                <Button
                  variant="outline"
                  colorScheme="red"
                  onClick={openTransferModal}
                  dataCy="business-details-transfer-ownership"
                >
                  Transfer ownership
                </Button>
                <Button
                  variant="solid"
                  colorScheme="red"
                  onClick={() => setIsRetireModalOpen(true)}
                >
                  Retire organization
                </Button>
              </div>
            </div>
          </PermissionsProvider>
        </>
      ),
    },
    {
      id: 'balances',
      title: 'Balances',
      group: 'Overview',
      content: (
        <BusinessTransactionsView
          business={businessDetails as AfriexBusiness}
          walletBalances={businessWallet}
          isLoadingWalletBalances={isBusinessWalletLoading}
        />
      ),
    },
    {
      id: 'configuration',
      title: 'Configuration',
      group: 'Configuration',
      content: (
        <SubTabs
          dataCy="business-details-configuration"
          tabs={[
            {
              id: 'allowed-features',
              label: 'Allowed Features',
              // The editor hydrates its tree once, on mount, so it is only
              // mounted once the business details have arrived: an editor
              // shown before then would be replaced on load, discarding any
              // selection made in it. Keying it on the id remounts it when
              // the screen switches to another business.
              content: businessDetails ? (
                <AllowedFeatureForm
                  key={businessDetails.id}
                  businessId={businessDetails.id ?? ''}
                  initialAllowedFeatures={
                    businessDetails.meta?.allowedFeatures ?? []
                  }
                />
              ) : (
                <div className="flex w-full h-full justify-center items-center my-5">
                  <SpinningLoader />
                </div>
              ),
            },
          ]}
        />
      ),
    },
    {
      id: 'api-settings',
      title: 'API & Webhooks',
      group: 'Configuration',
      content: <BusinessApiSettingsTab business={businessDetails} />,
    },
    {
      id: 'rfi',
      title: 'Request For Information',
      group: 'Compliance',
      content: (
        <PermissionsProvider permission={AfriexAdminPermission.RFIRead}>
          <BusinessRFITab
            dataCy="business-details-rfi"
            businessId={businessDetails?.id ?? ''}
            businessName={businessDetails?.name ?? ''}
          />
        </PermissionsProvider>
      ),
    },
    {
      id: 'virtual-accounts',
      title: 'Virtual Accounts',
      group: 'Compliance',
      content: (
        <BusinessVirtualAccountsTab businessId={businessDetails?.id ?? ''} />
      ),
    },
    {
      // The agreement the business reached on its portal Pricing page: the
      // signed MSA, or the negotiated agreement and its uploads.
      id: 'agreement',
      title: 'Agreement',
      group: 'Compliance',
      content: <BusinessAgreementTab businessId={businessDetails?.id ?? ''} />,
    },
    {
      id: 'invoices',
      title: 'Invoices',
      group: 'Financial',
      content: (
        <BusinessInvoicesTab
          dataCy="business-details-invoices"
          businessId={businessDetails?.id ?? ''}
          businessName={businessDetails?.name}
        />
      ),
    },
    {
      id: 'fees-and-rates',
      title: 'Fees & Rates',
      group: 'Financial',
      content: (
        <SubTabs
          dataCy="business-details-fees-and-rates"
          tabs={[
            {
              id: 'fees',
              label: 'Fees',
              content: (
                <BusinessFeesTab
                  dataCy="business-details-fees"
                  businessId={businessDetails?.id ?? ''}
                  businessName={businessDetails?.name}
                />
              ),
            },
            {
              id: 'rates',
              label: 'Rates',
              content: (
                <BusinessRatesTab
                  dataCy="business-details-rates"
                  businessId={businessDetails?.id ?? ''}
                  businessName={businessDetails?.name}
                />
              ),
            },
            {
              // The priced corridors this business sees on its portal Fees page,
              // read-only, with the ones it ticked marked. The "Fees" subtab
              // beside it edits the rules these rows are priced from.
              id: 'fee-schedule',
              label: 'Fee schedule',
              content: (
                <BusinessFeeScheduleTab
                  dataCy="business-details-fee-schedule"
                  businessId={businessDetails?.id ?? ''}
                  businessName={businessDetails?.name}
                />
              ),
            },
          ]}
        />
      ),
    },
    {
      // "Limits" (BusinessLimitsTab) landed on main after this revamp branch
      // diverged, so it was never grouped into a SubTabs cluster. Keep it as its
      // own tab under the Financial group so the feature is preserved.
      id: 'limits',
      title: 'Limits',
      group: 'Financial',
      content: (
        <BusinessLimitsTab
          dataCy="business-details-limits"
          businessId={businessDetails?.id ?? ''}
          businessName={businessDetails?.name}
          isApprovedForFeeWallet={
            !!businessDetails?.meta?.flags?.isApprovedForFeeWallet
          }
          feeWalletOverdraftLimitUSD={
            businessDetails?.meta?.feeWalletOverdraftLimitUSD
          }
        />
      ),
    },
  ];
  const {currentTab, handleTabChange} = useTabs(businessTabs, 'tab');

  return (
    <main className="mt-10 mb-5 mx-4 md:mx-10">
      <Header
        dataCy="merchant-business-details-title"
        title={`Business Details`}
      />
      <SidebarTabs
        tabs={businessTabs}
        activeTabId={currentTab.id}
        onTabChange={handleTabChange}
      />

      <TransferOwnerModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        onConfirm={transferOwner}
        dataCy="business-details-ownership-modal"
        isLoading={isTransferringOwner}
        members={eligibleMembers}
      />
      <MoveMemberModal
        isOpen={isMoveMemberModalOpen}
        onClose={() => setIsMoveMemberModalOpen(false)}
        onConfirm={requestMemberMove}
        onSearchBusinesses={searchTargetBusinesses}
        isLoading={isMovingMember}
        members={activeTeamMembers}
      />
      <ConfirmationModal
        isOpen={isRetireModalOpen}
        title="Retire this organization?"
        label={`Deactivate ${
          businessDetails?.name ?? 'this organization'
        } and remove it from the organizations list? Only possible once it has no active members left. This is for consolidated duplicates and will not be reactivated.`}
        cancelAction={() => setIsRetireModalOpen(false)}
        cancelActionLabel="Not now"
        confirmAction={async () => {
          const ok = await retireOrg();
          if (ok !== false) {
            setIsRetireModalOpen(false);
          }
        }}
        confirmActionLabel="Retire Org"
        loading={isRetiringOrg}
        loadingLabel="Retiring..."
        width="w-96"
      />
    </main>
  );
};
